import { EditClientSchema } from '@/validations/client';
import { Capacitor } from '@capacitor/core';
import { IonContent, IonGrid, IonList, IonPage, IonRow } from '@ionic/react';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';

import styles from '@/pages/Clients/Clients.module.css';

import { Button } from '@/components/Button';
import { ChooseMeasurementModal } from '@/components/ChooseMeasurementModal/ChooseMeasurementModal';
import { ClientOverviewHeader } from '@/components/ClientHeaders';
import { ClientModalContent } from '@/components/Clients';
import { EmptySessionState } from '@/components/Clients/EmptySessionState';
import { DeleteClientModal } from '@/components/DeleteClientModal/DeleteClientModal';
import { Divider } from '@/components/Divider/Divider';
import { Icon } from '@/components/Icon';
import { CustomLoading } from '@/components/Loading/CustomLoading';
import { Modal } from '@/components/Modal/Modal';
import { NavItem } from '@/components/NavItem/NavItem';
import { NoInternet } from '@/components/NoInternet/NoInternet';
import { Popover } from '@/components/Popover/Popover';
import { SessionSection } from '@/components/SessionSection/SessionSection';
import { toast } from '@/components/Toaster/Toast';

import { isProduction, mergeClassNames } from '@/utils';
import { SessionTypeInDatabase } from '@/utils/getFirebaseSessionTypes';
import { getFirebaseSessionTypesByOrganizationId } from '@/utils/getFirebaseSessionTypesByOrganizationId';

import ClientSessionManager from '@/hooks/useClients/sessionManager';
import UserService from '@/hooks/useClients/userService';
import { useDatabase } from '@/hooks/useDatabase';
import { useIsOnline } from '@/hooks/useIsOnline';

import { useClientContext, useSessionContext } from '@/contexts';
import { useAuthContext } from '@/contexts/authContext';
import { useReportContext } from '@/contexts/reportContext';
import { useUserInfoContext } from '@/contexts/userInfoContext';

import { SessionCreationData } from '@/models';

interface ParamTypes {
  id: string;
}

interface LocationState {
  clientName?: string;
  clientId?: string;
  resetActiveSession?: boolean;
  measurementType: string;
}

export const ClientPage = () => {
  const { id } = useParams<ParamTypes>();
  const { reportInfoDict } = useReportContext();
  const isOnline = useIsOnline();
  const location = useLocation<LocationState>();
  const history = useHistory();
  const user = useAuthContext().state.userState?.user;
  const { setClientName, clientName, setClientId, clientId, activeSessionId, setActiveSessionId } = useClientContext();
  const { sessionsList, setSessionsList, isLoading, setIsLoading } = useSessionContext();
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [sessionTypes, setSessionTypes] = useState<SessionTypeInDatabase[]>();
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const { userInfo } = useUserInfoContext();
  const { getFirebaseClientByClientId } = useDatabase();
  const [isDeleteClientModalOpen, setIsDeleteClientModalOpen] = useState(false);

  useEffect(() => {
    // Push latest report info dict into the manager whenever your hook updates
    ClientSessionManager.updateReportInfoDict(reportInfoDict);
  }, [reportInfoDict]);

  useEffect(() => {
    async function getClient() {
      setIsLoadingClient(true);
      if (!id) return null;

      const newClient = await getFirebaseClientByClientId(id);

      if (!newClient) return null;

      setClientData({
        firstName: newClient?.firstName ?? '',
        lastName: newClient?.lastName ?? '',
        dateOfBirth: new Date(newClient?.dateOfBirth.seconds * 1000).toISOString(),
        email: newClient?.email ?? '',
      });

      setIsLoadingClient(false);
    }

    getClient();
  }, [id]);

  useEffect(() => {
    async function getSessionTypes() {
      const sessionTypes = await getFirebaseSessionTypesByOrganizationId(userInfo.organization_id);

      setSessionTypes(sessionTypes);
    }

    getSessionTypes();
  }, [userInfo.organization_id]);

  // Hide the start new session button if the app is not running on a native platform (i.e. web) AND the user is in production
  const hideStartNewSessionButton = isProduction && !Capacitor.isNativePlatform();

  useEffect(() => {
    if (location.state?.clientName) {
      setClientName(location.state.clientName);
      sessionStorage.setItem('clientName', location.state.clientName);
    }

    if (id) {
      setClientId(id);
      sessionStorage.setItem('clientId', id);
    }
  }, [location.state, id, setClientName, setClientId]);

  const name = location.state?.clientName ?? clientName ?? sessionStorage.getItem('clientName') ?? '';

  const clientID = id ?? clientId ?? sessionStorage.getItem('clientId') ?? undefined;

  // #region Fetch sessions
  useEffect(() => {
    if (!user?.uid || !id || !userInfo.organization_id) {
      toast({
        variant: 'error',
        title: 'Could not load sessions',
        description: 'Please contact support',
      });
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const unsubscribe = ClientSessionManager.getSessions(
      user.uid,
      clientID,
      userInfo.organization_id,
      (sessions, error) => {
        if (error) {
          toast({
            variant: 'error',
            title: 'Could not load sessions',
            description: error.message,
          });
          setIsLoading(false);
          return;
        }
        console.log('Fetched sessions:', sessions);
        setSessionsList(sessions);

        setIsLoading(false);
      },
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.uid, id, setSessionsList, setIsLoading, userInfo.organization_id]);

  useEffect(() => {
    if (!sessionsList || sessionsList.length === 0) return;

    // Explicit reset coming from navigation state
    if (location.state?.resetActiveSession) {
      setActiveSessionId(sessionsList[0].id);
      setIsFirstLoad(false);
      return;
    }

    // First load or when active id no longer exists in the list
    if (!activeSessionId || !sessionsList.some((s) => s.id === activeSessionId)) {
      setActiveSessionId(sessionsList[0].id);
    }
  }, [sessionsList, location.state?.resetActiveSession]);

  const handleToggleSession = (sessionId: string) => {
    if (sessionId !== activeSessionId) {
      setActiveSessionId(sessionId);
    }
  };
  // #endregion

  // Session data
  const [isChooseMeasurementModalOpen, setIsChooseMeasurementModalOpen] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  const shouldShowEmptySessionState = useMemo(
    () => !isLoading && (sessionsList.length === 0 || sessionsList.every((list) => !list.id)),
    [isLoading, sessionsList],
  );

  const handleChooseMeasurementModalClose = async (session: SessionTypeInDatabase | undefined) => {
    if (!session) throw new Error('No Session Type selected');

    setIsCreatingSession(true);

    // Create session in firebase
    const sessionData: SessionCreationData = {
      clientId: clientId ?? '',
      date: new Date().toISOString(),
      sessionTypeId: session.id,
      therapistId: user!.uid,
    };

    let sessionId;
    try {
      sessionId = await ClientSessionManager.createSession(sessionData);

      history.push({
        pathname: '/session',
        state: {
          clientName: name,
          sessionId,
          tests: session.Tests,
          session,
          ...sessionData,
        },
      });
    } catch (error) {
      console.log('Error creating Session', error);

      toast({
        variant: 'error',
        title: 'Could not create Session',
        description: 'Please contact support',
      });

      return;
    } finally {
      setIsCreatingSession(false);
    }
  };

  // TODO: Retry upload
  const handleRetryUpload = (sessionId: string) => {
    console.log('Retry upload', sessionId);

    toast({
      title: 'Retrying upload is not supported yet',
      description: 'Please contact support',
    });
  };

  const [clientData, setClientData] = useState({
    firstName: name ?? '',
    lastName: '',
    dateOfBirth: new Date().toISOString(),
    email: '',
  });

  const [isLoadingClient, setIsLoadingClient] = useState(true);

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  const handleCloseModal = () => {
    setIsClientModalOpen(false);
  };

  const handleInputChange = async (field: string, value: string) => {
    setClientData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' })); // Reset error message
  };

  const handleConfirm = async () => {
    if (!clientID) {
      toast({
        variant: 'error',
        title: 'Error updating client',
        description: "Something went wrong. Please contact support and mention error code 'ERR_CLIENTID_UNDEFINED'",
      });

      return;
    }

    const validatedData = EditClientSchema.safeParse(clientData);

    if (!validatedData.success) {
      setErrors({
        firstName: validatedData.error.issues.find((issue) => issue.path[0] === 'firstName')?.message ?? '',
        lastName: validatedData.error.issues.find((issue) => issue.path[0] === 'lastName')?.message ?? '',
        email: validatedData.error.issues.find((issue) => issue.path[0] === 'email')?.message ?? '',
      });

      return;
    }

    try {
      await UserService.updateClient(clientID, clientData);
      setClientName(`${clientData.firstName} ${clientData.lastName}`);
      setIsClientModalOpen(false);

      toast({
        variant: 'success',
        title: 'Client edit was successful',
        description: 'The client info has been updated',
      });
    } catch (error: unknown) {
      if (error instanceof Error) {
        toast({
          variant: 'error',
          title: 'Error updating client',
          description: error.message,
        });
      }
    }
  };

  const hasAnyError = useMemo(() => {
    return !!errors.email || !!errors.firstName || !!errors.lastName;
  }, [errors.email, errors.firstName, errors.lastName]);

  const handleArchive = async () => {
    if (!clientID) {
      toast({
        variant: 'error',
        title: 'Error archiving client',
        description: "Something went wrong. Please contact support and mention error code 'ERR_CLIENTID_UNDEFINED'",
      });

      return;
    }

    try {
      await UserService.archiveClient(clientID);
      history.push('/clients');

      toast({
        variant: 'success',
        title: 'Client delete successful',
        description: 'The client has been deleted',
      });
    } catch (error: unknown) {
      if (error instanceof Error) {
        toast({
          variant: 'error',
          title: 'Error archiving client',
          description: error.message,
        });
      }
    }
  };

  return (
    <>
      <IonPage className={styles.page} id='main'>
        <div className={styles.header}>
          <ClientOverviewHeader
            backButton={true}
            backTarget='/clients'
            title={name!}
            titleSlot={
              <Button id='trigger-client-popover' variant='tertiary'>
                <Icon name='menu' />
              </Button>
            }>
            {!shouldShowEmptySessionState && !hideStartNewSessionButton && (
              <Button
                className={styles.actionButton}
                disabled={isLoading}
                leadingIcon='plus'
                onClick={() => setIsChooseMeasurementModalOpen(true)}
                size='medium'
                variant='primary'>
                Start new session
              </Button>
            )}
          </ClientOverviewHeader>
        </div>

        <IonContent className={styles.content} color='light' id='main'>
          {!isOnline && (
            <div className={styles.loading}>
              <NoInternet
                description='We could not load your data, make sure you have a stable internet connection'
                title='You are currently offline'
              />
            </div>
          )}
          {isLoading && !shouldShowEmptySessionState && (
            <div className={styles.loading}>
              <CustomLoading />
            </div>
          )}

          {!isLoading && shouldShowEmptySessionState && (
            <EmptySessionState onClickEvent={() => setIsChooseMeasurementModalOpen(true)} />
          )}

          {!isLoading && !shouldShowEmptySessionState && sessionsList.length > 0 && (
            <div className={styles.sessions}>
              {sessionsList.map((session, index) => (
                <SessionSection
                  handleRetryUpload={handleRetryUpload}
                  isActive={session.id === activeSessionId}
                  key={session.id ?? index}
                  onToggle={() => handleToggleSession(session.id)}
                  session={session}
                  sessionIndex={index}
                  sessionType={sessionTypes?.find((type) => type.id === session.sessionTypeId)}
                  totalSessions={sessionsList.length}
                />
              ))}
            </div>
          )}
        </IonContent>

        <Popover
          alignment='start'
          className={mergeClassNames(styles.popover, styles.popoverUser)}
          dismissOnSelect={true}
          side='bottom'
          trigger='trigger-client-popover'
          triggerAction='click'>
          <IonList className={styles.popoverList}>
            <NavItem onClick={() => setIsClientModalOpen(true)}>Edit client</NavItem>
            <Divider />
            <NavItem isDanger onClick={() => setIsDeleteClientModalOpen(true)}>
              Delete client
            </NavItem>
          </IonList>
        </Popover>

        <ChooseMeasurementModal
          isLoading={isCreatingSession}
          isOpen={isChooseMeasurementModalOpen}
          onClose={handleChooseMeasurementModalClose}
          sessionTypes={sessionTypes ?? []}
          setShowModal={setIsChooseMeasurementModalOpen}
        />

        <Modal
          footer={
            <>
              <Button onClick={handleCloseModal} variant='secondary'>
                Cancel
              </Button>
              <Button onClick={handleConfirm} variant='primary'>
                Confirm
              </Button>
            </>
          }
          header='Edit client'
          isOpen={isClientModalOpen}
          onDidDismiss={handleCloseModal}
          style={{
            '--height': hasAnyError ? '375px' : '330px',
          }}>
          <ClientModalContent
            clientData={clientData}
            errors={errors}
            loading={isLoadingClient}
            mode='edit'
            onInputChange={handleInputChange}
          />
        </Modal>

        <DeleteClientModal
          clientName={clientName!}
          handleClose={() => setIsDeleteClientModalOpen(false)}
          isOpen={isDeleteClientModalOpen}
          onConfirm={() => handleArchive()}
        />
      </IonPage>
    </>
  );
};
